import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { forkJoin } from 'rxjs';
import { CreateSurveyResponse$Json$Params } from '../../../api/fn/survey-response/create-survey-response-json';
import { UpdateSurveyResponseAsync$Json$Params } from '../../../api/fn/survey-response/update-survey-response-async-json';
import { CreateSurveyVersion$Json$Params } from '../../../api/fn/survey-version/create-survey-version-json';
import { UpdateSurveyVersionAsync$Json$Params } from '../../../api/fn/survey-version/update-survey-version-async-json';
import { CreateSurvey$Json$Params } from '../../../api/fn/survey/create-survey-json';
import { UpdateSurvey$Json$Params } from '../../../api/fn/survey/update-survey-json';
import {
  ClientSurveyModel,
  ClientSurveyQuestionModel,
  ClientSurveyResponseModel,
  ClientSurveyTypeModel,
  ClientSurveyVersionModel,
  CreateSurveyResponseRequest,
  EmployeeModel,
  UpdateSurveyResponseRequest,
} from '../../../api/models';
import {
  AttorneyService,
  SurveyMethodService,
  SurveyQuestionService,
  SurveyResponseService,
  SurveyService,
  SurveyVersionService,
} from '../../../api/services';
import { AccountInfoService } from '../../../services/account-info.service';
import { ToastService } from '../../../services/toast.service';
import { DateTimeUtils } from '../../../utils/datetime.utils';
import { FormUtils } from '../../../utils/form.utils';
import { BaseAccountInfoComponent } from '../../BaseAccountInfo/BaseAccountInfo.component';

interface takeSurveyModalToggleOptions {
  surveyVersion: ClientSurveyVersionModel;
  survey: ClientSurveyModel;
}

@Component({
  selector: 'app-take-survey-modal',
  templateUrl: './take-survey-modal.component.html',
  styleUrls: ['./take-survey-modal.component.scss'],
})
export class TakeSurveyModalComponent
  extends BaseAccountInfoComponent
  implements OnInit
{
  @Input() matterId: number;
  @Input() surveyTypeId: number;
  @Input() surveyTypeDetails: ClientSurveyTypeModel;
  @Input() attorneyId: number | null | undefined;

  @Output() reloadParent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() addSurveyContact: EventEmitter<[boolean, boolean]> =
    new EventEmitter<[boolean, boolean]>();

  surveyVersion: ClientSurveyVersionModel;
  survey: ClientSurveyModel;

  isEditMode = false;

  takeSurveyModalTitle = 'Take Survey';

  isTakeSurveyFormLoaded = false;
  isTakeSurveyFormSending = false;
  isTakeSurveyModalVisible = false;

  surveyForm: FormGroup = new FormGroup({});
  surveyQuestions: ClientSurveyQuestionModel[] = [];
  surveyAttorneys: EmployeeModel[] = [];

  submitSurveyButtonText = 'Submit Survey';

  faCircleNotch = faCircleNotch;

  compareAttorneys = (a: any, b: any) => a === b;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private surveyService: SurveyService,
    private surveyMethodService: SurveyMethodService,
    private surveyQuestionService: SurveyQuestionService,
    private surveyVersionService: SurveyVersionService,
    private surveyResponseService: SurveyResponseService,
    private attorneyService: AttorneyService,
    protected override accountInfoService: AccountInfoService
  ) {
    super(accountInfoService);
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  toggleTakeSurveyModal(options?: takeSurveyModalToggleOptions) {
    this.isTakeSurveyModalVisible = !this.isTakeSurveyModalVisible;
    if (
      this.isTakeSurveyFormLoaded &&
      this.isTakeSurveyModalVisible &&
      !this.isEditMode &&
      !options
    ) {
      this.resetSurveyForm();
    } else {
      // Set the title.
      this.takeSurveyModalTitle = `Take ${
        this.surveyTypeDetails!.value
      } Survey`;

      // If this is 'edit' mode, we don't need to fetch the survey data because it is in the surveyVersion.responses array.
      if (options) {
        this.resetSurveyForm();
        if (options.surveyVersion.isDraft) {
          this.submitSurveyButtonText = 'Approve';
          this.takeSurveyModalTitle = `Take ${
            this.surveyTypeDetails!.value
          } Survey`;
        } else {
          this.submitSurveyButtonText = 'Save as New Survey Version';
          this.takeSurveyModalTitle = `Edit ${
            this.surveyTypeDetails!.value
          } Survey`;
        }
        this.takeSurveyModalTitle = `Edit ${
          this.surveyTypeDetails!.value
        } Survey`;
        this.isEditMode = true;
        this.survey = options.survey;
        this.surveyVersion = options.surveyVersion;
        this.surveyQuestions = this.surveyTypeDetails!.questions!;

        const surveyAttorneys$ = this.fetchSurveyAttorneys();

        forkJoin([surveyAttorneys$]).subscribe({
          next: ([surveyAttorneysResponse]) => {
            if (!surveyAttorneysResponse.body) {
              console.error(surveyAttorneysResponse.body);
              this.toastService.showToast(
                'danger',
                'Problem loading attorneys'
              );
            } else {
              this.surveyAttorneys = surveyAttorneysResponse.body;
            }

            // Create the form.
            this.CreateSurveyForm();
            // Patch the form values.
            this.PatchSurveyFormValues(options.surveyVersion.responses!);
            // Patch the survey attorney.
            if (this.surveyAttorneys.length > 0) {
              const attorneyMatch = this.surveyAttorneys.find(
                (x) => x.employeeId == options.survey.attorneyId
              );
              if (attorneyMatch) {
                this.surveyForm.patchValue({
                  surveyAttorney: attorneyMatch,
                });
              } else {
                this.surveyForm.patchValue({
                  surveyAttorney: null,
                });
              }
            }
            this.isTakeSurveyFormLoaded = true;
          },
          error: (error) => {
            console.error(error, this);
            this.toastService.showToast(
              'danger',
              'Problem loading survey data'
            );
          },
        });
      } else {
        this.isEditMode = false;
        this.fetchSurveyData();
      }
    }
  }

  updateSurveyVersionDraftStatus() {
    const params: UpdateSurveyVersionAsync$Json$Params = {
      surveyVersionId: this.surveyVersion.surveyVersionId!,
      body: {
        isDraft: false,
        completedBy: this.surveyVersion.completedBy!,
        dateUpdated: DateTimeUtils.getCurrentDateTimeFormattedForApi(),
        surveyVersionId: this.surveyVersion.surveyVersionId!,
      },
    };
    return this.surveyVersionService.updateSurveyVersionAsync$Json$Response(
      params
    );
  }

  fetchSurveyAttorneys() {
    return this.attorneyService.getAttorneys$Json$Response();
  }

  fetchSurveyMethods() {
    return this.surveyMethodService.getSurveyMethods$Json$Response();
  }

  fetchSurveyQuestions() {
    return this.surveyQuestionService.getSurveyQuestions$Json$Response({
      surveyTypeId: this.surveyTypeId,
    });
  }

  fetchSurveyData() {
    const surveyAttorneys$ = this.fetchSurveyAttorneys();
    const surveyQuestions$ = this.fetchSurveyQuestions();

    forkJoin([surveyAttorneys$, surveyQuestions$]).subscribe({
      next: ([surveyAttorneysResponse, surveyQuestionsResponse]) => {
        if (!surveyAttorneysResponse.body) {
          this.toastService.showToast('danger', 'Problem loading attorneys');
        } else {
          this.surveyAttorneys = surveyAttorneysResponse.body;
        }

        if (!surveyQuestionsResponse.body) {
          console.error(surveyQuestionsResponse.body); // Log the error for debugging purposes.
          this.toastService.showToast(
            'danger',
            'Problem loading survey questions'
          );
        } else {
          this.surveyQuestions = surveyQuestionsResponse.body;
          this.CreateSurveyForm();

          const attorneyMatch = this.surveyAttorneys.find(
            (x) => x.employeeId == this.attorneyId
          );
          if (attorneyMatch) {
            this.surveyForm.patchValue({
              surveyAttorney: attorneyMatch,
            });
          } else {
            this.surveyForm.patchValue({
              surveyAttorney: null,
            });
          }
          this.isTakeSurveyFormLoaded = true;
        }
      },
      error: (error) => {
        console.error(error, this);
        this.toastService.showToast('danger', 'Problem loading survey data');
      },
    });
  }

  CreateSurveyForm() {
    this.surveyForm.addControl(
      'surveyAttorney',
      this.fb.control(null, Validators.required)
    );

    if (this.surveyAttorneys.length > 0 && this.attorneyId) {
      const attorneyMatch = this.surveyAttorneys.find(
        (x) => x.employeeId == this.attorneyId
      );
      if (attorneyMatch) {
        this.surveyForm.patchValue({
          surveyAttorney: attorneyMatch,
        });
      } else {
        this.surveyForm.patchValue({
          surveyAttorney: null,
        });
      }
    }
    this.surveyQuestions.forEach((q) => {
      this.surveyForm.addControl(
        q.surveyQuestionId!.toString(),
        this.fb.control('')
      );
      this.surveyForm.addControl(
        q.surveyQuestionId!.toString() + '_comments',
        this.fb.control('')
      );
      this.surveyForm.addControl(
        'responseid_' + q.surveyQuestionId!.toString(),
        this.fb.control('')
      );
    });
  }

  PatchSurveyFormValues(responses: ClientSurveyResponseModel[]) {
    responses.forEach((x) => {
      if (x.questionType === 'R') {
        if (x.rating !== null && x.rating !== undefined) {
          this.surveyForm.patchValue({
            [x.surveyQuestionId!.toString()]: x.rating,
          });
        }
        else if (x.textResponse === 'N') {
          this.surveyForm.patchValue({
            [x.surveyQuestionId!.toString()]: 0,
          });
        }
      }
      else if (x.questionType === 'T') {
        if (x.textResponse !== null && x.textResponse !== undefined) {
          this.surveyForm.patchValue({
            [x.surveyQuestionId!.toString()]: x.textResponse,
          });
        }
      }
      else if (x.questionType === 'B') {
        if ((x.boolean == null || x.boolean == undefined) && x.textResponse === 'NA') {
          this.surveyForm.patchValue({
            [x.surveyQuestionId!.toString()]: 'NA',
          });
        }
        else {
          this.surveyForm.patchValue({
            [x.surveyQuestionId!.toString()]: x.boolean,
          });
        }
      }

      this.surveyForm.patchValue({
        [x.surveyQuestionId!.toString() + '_comments']: x.comments,
      });
      this.surveyForm.patchValue({
        ['responseid_' + x.surveyQuestionId!.toString()]: x.surveyReponseId,
      });
    });
  }

  resetSurveyForm() {
    const defaultValues: { [key: string]: any } = {};

    this.surveyQuestions.forEach((q) => {
      defaultValues[q.surveyQuestionId!.toString()] = '';
    });

    this.surveyForm.reset(defaultValues);
  }

  submitSurveyForm() {
    if (this.surveyForm.valid) {
      this.isTakeSurveyFormSending = true;

      if (this.isEditMode) {
        // If this is currently a draft, save the CURRENT survey version as Not Draft.
        if (this.surveyVersion.isDraft) {
          this.surveyVersion.isDraft = false;
          this.updateSurveyVersionDraftStatus().subscribe({
            next: (r) => {
              if (!r.body) {
                console.error(r.body);
                this.toastService.showToast(
                  'danger',
                  'Problem updating survey version'
                );
              } else {
                this.updateSurveyResponses(this.surveyVersion.surveyVersionId!);
              }
            },
            error: (error) => {
              console.error(error, this);
              this.isTakeSurveyFormSending = false;
              this.toastService.showToast(
                'danger',
                'Problem updating survey version'
              );
            },
          });
        } else {
          // Update the survey responses
          this.updateSurveyRecord();
        }
      } else {
        // Create new survey responses
        this.createSurveyRecord();
      }

      this.addSurveyContact.emit([false, true]); // Notify the parent so they can reload data.
    }
  }

  updateSurveyRecord() {
    let params: UpdateSurvey$Json$Params = {
      surveyId: this.survey.surveyId!,
      body: {
        surveyId: this.survey.surveyId!,
        attorneyId: this.surveyForm.value.surveyAttorney.employeeId,
        caseId: this.survey.caseId!,
        surveyTypeId: this.survey.surveyTypeId!,
        surveyDate: this.survey.surveyDate!,
      },
    };

    this.surveyService.updateSurvey$Json$Response(params).subscribe({
      next: (r) => {
        if (!r.body) {
          console.error(r.body);
          this.toastService.showToast('danger', 'Problem updating survey');
        } else {
          this.createSurveyVersion(this.survey.surveyId!);
        }
      },
      error: (error) => {
        console.error(error, this);
        this.isTakeSurveyFormSending = false;
        this.toastService.showToast('danger', 'Problem saving survey');
      },
    });
  }

  createSurveyRecord() {
    let params: CreateSurvey$Json$Params = {
      body: {
        attorneyId: this.surveyForm.value.surveyAttorney.employeeId,
        caseId: this.matterId,
        surveyTypeId: this.surveyTypeId,
        surveyDate: moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      },
    };

    // First create a new survey.
    this.surveyService.createSurvey$Json$Response(params).subscribe({
      next: (r) => {
        if (!r.body) {
          console.error(r.body);
          this.toastService.showToast('danger', 'Problem saving survey');
        } else {
          if (!r.body.surveyId) {
            console.error(r.body);
            this.toastService.showToast('danger', 'Problem saving survey');
          } else {
            this.createSurveyVersion(r.body.surveyId!);
          }
        }
      },
      error: (error) => {
        console.error(error, this);
        this.isTakeSurveyFormSending = false;
        this.toastService.showToast('danger', 'Problem saving survey');
      },
    });
  }

  createSurveyVersion(surveyId: number) {
    let params: CreateSurveyVersion$Json$Params = {
      body: {
        isDraft: false,
        completedBy: this.accountInfo!.username,
        dateCreated: DateTimeUtils.getCurrentDateTimeFormattedForApi(),
        surveyId: surveyId,
      },
    };

    // Create a survey version.
    this.surveyVersionService
      .createSurveyVersion$Json$Response(params)
      .subscribe({
        next: (r) => {
          if (!r.body) {
            console.error(r.body);
            this.toastService.showToast(
              'danger',
              'Problem creating survey version'
            );
          } else {
            this.createSurveyResponses(surveyId, r.body.surveyVersionId!);
          }
        },
        error: (error) => {
          console.error(error, this);
          this.isTakeSurveyFormSending = false;
          this.toastService.showToast(
            'danger',
            'Problem creating survey version'
          );
        },
      });
  }

  updateSurveyResponses(surveyVersionId: number) {
    const surveyResponses: UpdateSurveyResponseRequest[] =
      this.surveyQuestions.map((q) => {
        let data: UpdateSurveyResponseRequest = {
          surveyResponseId:
            this.surveyForm.value[
              'responseid_' + q.surveyQuestionId!.toString()
            ],
          surveyQuestionId: q.surveyQuestionId!,
          surveyVersionId: surveyVersionId,
          textResponse: FormUtils.evaluateResponseTextByType(
            q.type!,
            this.surveyForm.value[q.surveyQuestionId!.toString()]
          ),
          rating:
            q.type == 'R'
              ? this.surveyForm.value[q.surveyQuestionId!.toString()] === ''
                ? null
                : this.surveyForm.value[q.surveyQuestionId!.toString()] == '0'
                ? null
                : this.surveyForm.value[q.surveyQuestionId!.toString()]
              : null,
          comments:
            this.surveyForm.value[q.surveyQuestionId!.toString() + '_comments'],
          boolean: FormUtils.evaluateResponseBooleanByType(
            q.type!,
            this.surveyForm.value[q.surveyQuestionId!.toString()]
          ),
        };
        return data;
      });

    let posts$: any = [];

    surveyResponses.forEach((r) => {
      let params: UpdateSurveyResponseAsync$Json$Params = {
        surveyResponseId: r.surveyResponseId,
        body: r,
      };
      posts$.push(
        this.surveyResponseService.updateSurveyResponseAsync$Json$Response(
          params
        )
      );
    });

    forkJoin(posts$).subscribe({
      next: (r: any) => {
        this.toastService.showToast(
          'success',
          'responses submitted successfully'
        );
        this.reloadParent.emit(true); // Notify the parent so they can reload data.
        this.isTakeSurveyFormSending = false;
        this.toggleTakeSurveyModal();
      },
      error: (error) => {
        console.error(error, this);
        this.toastService.showToast('danger', 'Problem submitting responses');
        this.isTakeSurveyFormSending = false;
        this.toggleTakeSurveyModal();
      },
    });
  }

  createSurveyResponses(surveyId: number, surveyVersionId: number) {
    const surveyResponses: CreateSurveyResponseRequest[] =
      this.surveyQuestions.map((q) => {
        let data: CreateSurveyResponseRequest = {
          surveyQuestionId: q.surveyQuestionId!,
          surveyVersionId: surveyVersionId,
          textResponse: FormUtils.evaluateResponseTextByType(
            q.type!,
            this.surveyForm.value[q.surveyQuestionId!.toString()]
          ),
          rating:
            q.type == 'R'
              ? this.surveyForm.value[q.surveyQuestionId!.toString()] === ''
                ? null
                : this.surveyForm.value[q.surveyQuestionId!.toString()] == '0'
                  ? null
                  : this.surveyForm.value[q.surveyQuestionId!.toString()]
              : null,
          comments:
            this.surveyForm.value[q.surveyQuestionId!.toString() + '_comments'],
          boolean: FormUtils.evaluateResponseBooleanByType(
            q.type!,
            this.surveyForm.value[q.surveyQuestionId!.toString()]
          ),
        };
        return data;
      });

    let posts$: any = [];

    surveyResponses.forEach((r) => {
      let params: CreateSurveyResponse$Json$Params = {
        body: r,
      };
      posts$.push(
        this.surveyResponseService.createSurveyResponse$Json$Response(params)
      );
    });

    forkJoin(posts$).subscribe({
      next: (r: any) => {
        this.toastService.showToast('success', 'Survey submitted successfully');
        this.reloadParent.emit(true); // Notify the parent so they can reload data.
        this.isTakeSurveyFormSending = false;
        this.toggleTakeSurveyModal();
      },
      error: (error) => {
        console.error(error, this);
        this.toastService.showToast('danger', 'Problem submitting survey');
        this.isTakeSurveyFormSending = false;
        this.toggleTakeSurveyModal();
      },
    });
  }
}
